/**
 * FormBuilderPage — `/service-desk/forms`.
 *
 * Slice B10: form definitions are persisted via POST/PUT/DELETE
 * /v1/service-desk/forms. The palette + canvas UI is retained; on Save
 * the schema is POSTed to the backend (or PUT if editing an existing def).
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlignLeft,
  Calendar,
  CheckSquare,
  ChevronDown,
  Edit2,
  Fingerprint,
  Hash,
  List,
  type LucideIcon,
  Mail,
  MapPin,
  Phone,
  Plus,
  Save,
  Trash2,
  Type,
  Upload,
  X,
} from 'lucide-react';

import {
  createFormDefinition,
  deleteFormDefinition,
  listFormDefinitions,
  updateFormDefinition,
  type FormDefinition,
} from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';

interface FieldDef {
  readonly id: string;
  readonly type: string;
  readonly label: string;
  readonly required: boolean;
}

interface PaletteItem {
  readonly type: string;
  readonly label: string;
  readonly icon: LucideIcon;
}

const PALETTE: readonly PaletteItem[] = [
  { type: 'text',      label: 'Text',         icon: Type },
  { type: 'number',    label: 'Number',       icon: Hash },
  { type: 'email',     label: 'Email',        icon: Mail },
  { type: 'phone',     label: 'Phone',        icon: Phone },
  { type: 'date',      label: 'Date',         icon: Calendar },
  { type: 'select',    label: 'Select',       icon: ChevronDown },
  { type: 'multi',     label: 'Multi-select', icon: List },
  { type: 'file',      label: 'File upload',  icon: Upload },
  { type: 'checkbox',  label: 'Checkbox',     icon: CheckSquare },
  { type: 'textarea',  label: 'Textarea',     icon: AlignLeft },
  { type: 'nin',       label: 'NIN',          icon: Fingerprint },
  { type: 'address',   label: 'Address',      icon: MapPin },
];

export function FormBuilderPage(): JSX.Element {
  const queryClient = useQueryClient();

  // --- Saved definitions list ---
  const { data: formList, isPending: listLoading } = useQuery({
    queryKey: ['service-desk', 'forms'],
    queryFn: () => listFormDefinitions(),
  });

  // --- Canvas state ---
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('New Form');
  const [fields, setFields] = useState<readonly FieldDef[]>([]);

  // Reset canvas when starting new
  function startNew(): void {
    setEditingId(null);
    setName('New Form');
    setFields([]);
  }

  function loadForEdit(def: FormDefinition): void {
    setEditingId(def.id);
    setName(def.name);
    const raw = def.fieldsSchema as Record<string, unknown>;
    const arr = Array.isArray(raw['fields']) ? (raw['fields'] as FieldDef[]) : [];
    setFields(arr);
  }

  const createMut = useMutation({
    mutationFn: (body: { name: string; fieldsSchema: Record<string, unknown> }) =>
      createFormDefinition(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'forms'] });
      startNew();
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; fieldsSchema: Record<string, unknown> }) =>
      updateFormDefinition(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'forms'] });
      startNew();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFormDefinition(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'forms'] });
      startNew();
    },
  });

  function addField(type: string, label: string): void {
    const id = `f-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    setFields((prev) => [...prev, { id, type, label, required: false }]);
  }

  function removeField(id: string): void {
    setFields((prev) => prev.filter((f) => f.id !== id));
  }

  function handleSave(): void {
    const body = { name, fieldsSchema: { fields } };
    if (editingId) {
      updateMut.mutate({ id: editingId, ...body });
    } else {
      createMut.mutate(body);
    }
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const saveError = createMut.error ?? updateMut.error;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Form builder"
        description="Design third-party intake forms — persisted to backend."
      />

      {saveError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {saveError instanceof Error ? saveError.message : 'Unknown error'}
        </div>
      ) : null}

      {/* Saved definitions list */}
      {formList && formList.items.length > 0 ? (
        <div className="bg-white border border-border rounded-xl divide-y divide-border">
          <p className="px-4 py-3 text-xs font-semibold text-muted uppercase tracking-wider">
            Saved definitions ({formList.total})
          </p>
          {formList.items.map((def) => (
            <div key={def.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium">{def.name}</p>
                <p className="text-xs text-muted font-mono">{def.id}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => loadForEdit(def)}
                  className="p-1.5 rounded hover:bg-gray-100 text-muted hover:text-nipost-green"
                  aria-label={`Edit ${def.name}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteMut.mutate(def.id)}
                  className="p-1.5 rounded hover:bg-gray-100 text-muted hover:text-red-600"
                  aria-label={`Delete ${def.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : listLoading ? (
        <p className="text-sm text-muted">Loading saved forms…</p>
      ) : null}

      {/* Canvas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Palette */}
        <div className="lg:col-span-3 bg-white border border-border rounded-xl p-4 space-y-2">
          <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Palette</p>
          {PALETTE.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.type}
                type="button"
                onClick={() => addField(p.type, p.label)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm hover:bg-nipost-green-light hover:border-nipost-green/30"
              >
                <Icon className="w-4 h-4 text-muted" />
                <span className="flex-1 text-left">{p.label}</span>
                <Plus className="w-3.5 h-3.5 text-muted" />
              </button>
            );
          })}
        </div>

        {/* Canvas area */}
        <div className="lg:col-span-9 bg-white border border-border rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted mb-1">Form name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-medium"
              />
            </div>
            <div className="flex items-center gap-2 pt-5">
              {editingId ? (
                <button
                  type="button"
                  onClick={startNew}
                  className="p-2 rounded-lg border border-border hover:bg-gray-100"
                  aria-label="Cancel edit"
                >
                  <X className="w-4 h-4 text-muted" />
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !name.trim()}
                className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving…' : editingId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>

          {fields.length === 0 ? (
            <p className="text-sm text-muted text-center py-12 bg-gray-50 rounded-lg border border-dashed border-border">
              Click a palette item on the left to add a field.
            </p>
          ) : (
            <ul className="space-y-2">
              {fields.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-3 py-2 border border-border rounded-lg">
                  <span className="text-[10px] font-semibold uppercase text-muted">{f.type}</span>
                  <input
                    type="text"
                    value={f.label}
                    onChange={(e) =>
                      setFields((prev) =>
                        prev.map((x) => (x.id === f.id ? { ...x, label: e.target.value } : x)),
                      )
                    }
                    className="flex-1 px-2 py-1 border border-border rounded text-sm"
                  />
                  <label className="text-xs flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={f.required}
                      onChange={(e) =>
                        setFields((prev) =>
                          prev.map((x) => (x.id === f.id ? { ...x, required: e.target.checked } : x)),
                        )
                      }
                    />
                    required
                  </label>
                  <button
                    type="button"
                    onClick={() => removeField(f.id)}
                    aria-label={`Remove ${f.label}`}
                    className="p-1 rounded hover:bg-gray-100 text-muted hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
